import { Button, Stack, Text } from '@mantine/core';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { getGetInvoiceQueryKey, getListInvoicesQueryKey } from '@/api/generated/invoices/invoices';
import { getListPaymentsQueryKey, useRecordPayment } from '@/api/generated/payments/payments';
import {
  CreatableSelectField,
  DateField,
  FormModal,
  ModalFooter,
  MutationButton,
  NumberField,
  TextareaField,
  TextField
} from '@/components/common';
import { PAYMENT_METHOD_PRESET_KEYS } from '@/features/invoices/invoiceDisplay';
import { formatAmount } from '@/utils/format';

const today = () => new Date().toISOString().slice(0, 10);

interface RecordPaymentModalProps {
  opened: boolean;
  onClose: () => void;
  invoiceId: string;
  /** Preostali iznos fakture (ukupno − plaćeno) — predlaže se kao iznos uplate pri otvaranju. */
  remainingAmount?: number;
  onRecorded: (message: string) => void;
}

export const RecordPaymentModal = ({
  opened,
  onClose,
  invoiceId,
  remainingAmount,
  onRecorded
}: RecordPaymentModalProps) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [amount, setAmount] = useState<number | ''>('');
  const [paymentDate, setPaymentDate] = useState<string | null>(today());
  const [paymentMethod, setPaymentMethod] = useState('');
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');

  // Najčešće se plaća cijeli ostatak — predloži ga pri svakom otvaranju, korisnik ga može
  // promijeniti (djelomična uplata).
  useEffect(() => {
    if (!opened) return;
    const remaining = Math.round((remainingAmount ?? 0) * 100) / 100;
    setAmount(remaining > 0 ? remaining : '');
  }, [opened, remainingAmount]);

  const reset = () => {
    setAmount('');
    setPaymentDate(today());
    setPaymentMethod('');
    setReference('');
    setNote('');
  };

  const recordMutation = useRecordPayment({
    mutation: {
      meta: { skipGlobalErrorToast: true },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: getListPaymentsQueryKey(invoiceId) });
        queryClient.invalidateQueries({ queryKey: getGetInvoiceQueryKey(invoiceId) });
        queryClient.invalidateQueries({ queryKey: getListInvoicesQueryKey() });
        onRecorded(
          t('invoiceDetail.recordPaymentModal.success', {
            amount: formatAmount(typeof amount === 'number' ? amount : undefined)
          })
        );
        reset();
        onClose();
      }
    }
  });

  const handleSubmit = () => {
    if (!amount || amount <= 0 || !paymentDate) return;
    recordMutation.mutate({
      invoiceId,
      data: {
        amount,
        paymentDate,
        paymentMethod: paymentMethod.trim() || undefined,
        reference: reference.trim() || undefined,
        note: note.trim() || undefined
      }
    });
  };

  return (
    <FormModal
      opened={opened}
      onClose={() => {
        reset();
        onClose();
      }}
      title={
        <Text fz={18} fw={700} style={{ color: 'var(--color-text-primary)' }}>
          {t('invoiceDetail.recordPaymentModal.title')}
        </Text>
      }
      styles={{
        content: { backgroundColor: 'var(--color-bg-surface)' },
        header: {
          backgroundColor: 'var(--color-bg-surface)',
          borderBottom: '1px solid var(--color-border-soft)'
        },
        body: { padding: 20 }
      }}
    >
      <Stack gap={16}>
        <NumberField
          label={t('invoiceDetail.recordPaymentModal.fieldAmount')}
          required
          min={0.01}
          decimalScale={2}
          // Valuta kao i svugdje u prikazu iznosa (formatAmount) — dio prikaza, vrijednost ostaje broj.
          suffix=" EUR"
          value={amount}
          onChange={(value) => setAmount(typeof value === 'number' ? value : '')}
        />
        <DateField
          label={t('invoiceDetail.recordPaymentModal.fieldDate')}
          required
          valueFormat="DD.MM.YYYY."
          value={paymentDate}
          onChange={setPaymentDate}
        />
        <CreatableSelectField
          label={t('invoiceDetail.recordPaymentModal.fieldMethod')}
          placeholder={t('invoiceDetail.recordPaymentModal.methodPlaceholder')}
          data={PAYMENT_METHOD_PRESET_KEYS.map((key) => t(key))}
          value={paymentMethod}
          onChange={setPaymentMethod}
          createLabel={(query) =>
            t('invoiceDetail.recordPaymentModal.methodCreate', { value: query })
          }
        />
        <TextField
          label={t('invoiceDetail.recordPaymentModal.fieldReference')}
          value={reference}
          onChange={(event) => setReference(event.currentTarget.value)}
        />
        <TextareaField
          label={t('invoiceDetail.recordPaymentModal.fieldNote')}
          minRows={2}
          value={note}
          onChange={(event) => setNote(event.currentTarget.value)}
        />

        {recordMutation.isError && (
          <Text fz={13} style={{ color: 'var(--color-status-danger)' }}>
            {t('invoiceDetail.recordPaymentModal.error')}
          </Text>
        )}

        <ModalFooter>
          <Button
            variant="outline"
            onClick={() => {
              reset();
              onClose();
            }}
          >
            {t('common.cancel')}
          </Button>
          <MutationButton
            isPending={recordMutation.isPending}
            isPaused={recordMutation.isPaused}
            disabled={!amount || amount <= 0 || !paymentDate}
            onClick={handleSubmit}
          >
            {t('invoiceDetail.recordPaymentModal.submit')}
          </MutationButton>
        </ModalFooter>
      </Stack>
    </FormModal>
  );
};
